import nodeLibrary from './nodeLibrary.json';
import workflowSchema from './workflowSchema.json';

const { nodes, typeColors } = nodeLibrary;

// Get node definition helper
export function getNodeDefinition(nodeId) {
  return nodes.find(n => n.id === nodeId) || null;
}

export function getIconBg(nodeId) {
  const node = nodes.find(n => n.id === nodeId);
  if (node) {
    const color = typeColors[node.type] || '#6b7280';
    return `${color}15`;
  }
  const fixed = {
    prompt: 'rgba(34, 197, 94, 0.15)',
    response: 'rgba(239, 68, 68, 0.15)',
  };
  return fixed[nodeId] || '#f3f4f6';
}

export function getDefaultConfig(nodeId, nodeObj) {
  // Fixed configs for start/end nodes from schema
  const schemaConfig = workflowSchema.defaultNodeConfigs[nodeId];
  if (schemaConfig) return { ...schemaConfig };

  // For all other nodes, seed config from the node definition
  const node = nodes.find(n => n.id === nodeId) || nodeObj;
  if (node) {
    return {
      template: node.template || '',
      title: node.name || '',
      description: node.description || '',
      icon: node.icon || '',
      skillName: node.id || '',
      author_username: node.author_username || '',
      source_link: node.source_link || '',
      category: node.type || 'custom'
    };
  }
  return { template: '' };
}

// Parse YAML frontmatter from a markdown string
export function parseFrontmatter(content) {
  if (!content || typeof content !== 'string') return {};
  const match = content.match(/^---\n([\s\S]*?)\n---/);
  if (!match) return {};
  const fm = {};
  match[1].split('\n').forEach(line => {
    const idx = line.indexOf(':');
    if (idx > 0) {
      const key = line.slice(0, idx).trim();
      let val = line.slice(idx + 1).trim();
      // Strip surrounding quotes
      if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
        val = val.slice(1, -1);
      }
      fm[key] = val;
    }
  });
  return fm;
}

// Convert a display title to a skill name (lowercase, dashes)
function toTitleCase(str) {
  return str.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Build workflow steps from detected skill files
// skills: array of { path: '.claude/skills/foo.md', content: '---\nname: ...\n---\n...' }
export function buildStepsFromSkills(skills) {
  // Deduplicate by filepath and skill name
  const seenPaths = new Set();
  const seenNames = new Set();
  return skills.filter(skill => {
    const skillPath = typeof skill === 'string' ? skill : skill.path;
    const skillContent = typeof skill === 'string' ? '' : (skill.content || '');
    const fm = parseFrontmatter(skillContent);
    const name = fm.name || skillPath.replace('.claude/skills/', '').replace('/SKILL.md', '').replace('.md', '');
    if (seenPaths.has(skillPath) || seenNames.has(name)) return false;
    seenPaths.add(skillPath);
    seenNames.add(name);
    return true;
  }).map((skill, i) => {
    // Handle both string paths (old format) and { path, content } objects
    const skillPath = typeof skill === 'string' ? skill : skill.path;
    const skillContent = typeof skill === 'string' ? '' : (skill.content || '');
    const fm = parseFrontmatter(skillContent);
    const skillName = fm.name || skillPath.replace('.claude/skills/', '').replace('/SKILL.md', '').replace('.md', '');
    const title = toTitleCase(skillName);
    const description = fm.description || '';

    // Icon only from the skill file frontmatter
    const icon = fm.icon || '';
    const knownNode = nodes.find(n => n.id === skillName);
    const color = knownNode ? (typeColors[knownNode.type] || '#8b5cf6') : '#8b5cf6';

    return {
      id: `skill-${skillName}-${Date.now()}-${i}`,
      nodeId: skillName,
      name: title,
      subtitle: description,
      icon,
      iconBg: `${color}15`,
      parentNode: null, // will be wired after
      filepath: skillPath,
      config: {
        template: skillContent,
        title,
        description,
        icon,
        skillName
      },
      fileIds: []
    };
  });
}

// Build full default steps with detected skills inserted between User Input and Agent Modification
export function buildDefaultStepsWithSkills(skills = []) {
  const { loopStart, userInput, agentModification } = workflowSchema.defaultSteps;

  const loopStartStep = { ...loopStart };
  const userInputStep = { ...userInput };
  const agentMod = { ...agentModification };

  const skillSteps = buildStepsFromSkills(skills);

  // Wire parentNode chain: userInput → skill1 → skill2 → ... → agentMod
  let prevId = userInputStep.id;
  skillSteps.forEach(step => {
    step.parentNode = prevId;
    prevId = step.id;
  });
  agentMod.parentNode = prevId;

  return [loopStartStep, userInputStep, ...skillSteps, agentMod];
}

// Default steps for a new workflow
export const defaultSteps = buildDefaultStepsWithSkills();
